// This month's actions (Accelerator.dc.html): the actions Adam set at the session, which the member
// ticks off as they go (a tick is struck through; the database stamps it), each carried-over 1
// with the month it came from, how many are ticked and when they're due (due: "Due at your session on
// Sun 25 Oct."), and 1 footer under them, the same on the home and the Actions page and none with no actions.
// Each takes the member's note for Adam on how it's going, on the home as on the Actions page (the Accelerator
// critique's members 6 and 7, 20 Oct). A month that has closed can't be changed; while staff view as a member
// nothing can be ticked or noted.
import { useState } from 'react'
import { readOnlyControl } from '../../lib/viewAs.jsx'
import { COPY, SAVE_FAILED } from './copy.js'
import { actionError } from './model.js'
import { saveNote, tickAction } from './data.js'
import { LIMITS } from './limits.js'

const put = (d, row) => ({ ...d, actions: d.actions.map(a => (a.id === row.id ? { ...a, ...row } : a)) })

export function ActionsCard({ month, viewingAs, patch, notes = false, due = null, headingLevel = 'h2' }) {
  const [line, setLine] = useState(null)
  const H = headingLevel

  function toggle(row, done) {
    if (viewingAs) return
    setLine(null)
    const at = done ? new Date().toISOString() : null
    patch(d => put(d, { id: row.id, done_at: at }))
    tickAction(row.id, done)
      .then(saved => patch(d => put(d, saved)))
      .catch(err => {
        patch(d => put(d, { id: row.id, done_at: done ? null : row.doneAt }))
        setLine(actionError(err) || SAVE_FAILED)
      })
  }

  return (
    <section className="acc-card acc-actions" aria-labelledby="acc-actions-h">
      <div className="acc-actions-head">
        <div className="acc-titles">
          <H className="acc-h2" id="acc-actions-h">{COPY.thisMonth}</H>
          {month && <span className="acc-sub">{month.line}</span>}
        </div>
        {month && month.rows.length > 0 && (
          <span className="acc-counts">
            <span className="acc-count">{month.count}</span>
            {due && <span className="acc-due">{due}</span>}
          </span>
        )}
      </div>
      {(!month || !month.rows.length) && <p className="acc-actions-none">{COPY.noActions}</p>}
      {month && month.rows.map(row => (
        <div key={row.id} className={row.done ? 'acc-action acc-action--done' : 'acc-action'}>
          <label className="acc-action-tick">
            <input type="checkbox" checked={row.done} onChange={e => toggle(row, e.target.checked)} {...readOnlyControl(viewingAs)} />
            <span className="acc-action-body">
              <span className="acc-action-text">{row.body}</span>
              {row.carriedFrom && <span className="acc-badge">{row.carriedFrom}</span>}
            </span>
          </label>
          {notes && <NoteEditor row={row} viewingAs={viewingAs} patch={patch} />}
        </div>
      ))}
      {line && <p className="acc-msg acc-actions-msg" role="alert">{line}</p>}
      {month && month.rows.length > 0 && <p className="acc-actions-foot">{COPY.actionsFoot}</p>}
    </section>
  )
}

// The member's note on 1 action (the Actions page)
function NoteEditor({ row, viewingAs, patch }) {
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [line, setLine] = useState(null)

  function start() {
    if (viewingAs) return
    setText(row.note)
    setLine(null)
    setOpen(true)
  }

  async function save(e) {
    e.preventDefault()
    if (viewingAs || busy) return
    const note = text.trim()
    if (note.length > LIMITS.note) { setLine(COPY.noteLength); return }
    setBusy(true)
    setLine(null)
    try {
      const saved = await saveNote(row.id, note)
      patch(d => put(d, saved))
      setOpen(false)
    } catch (err) {
      setLine(actionError(err) || (err?.code === '23514' ? COPY.noteLength : SAVE_FAILED))
    } finally {
      setBusy(false)
    }
  }

  if (open) {
    return (
      <form className="acc-note-form" onSubmit={save} noValidate>
        <textarea aria-label={COPY.noteField} placeholder={COPY.notePlaceholder} value={text} maxLength={LIMITS.note} rows={3}
          onChange={e => { setText(e.target.value); setLine(null) }} autoFocus />
        <div className="acc-row">
          <button type="submit" className="acc-btn acc-btn--small" aria-busy={busy || undefined}>{COPY.saveNote}</button>
          <button type="button" className="acc-btn-line acc-btn--small" onClick={() => setOpen(false)}>{COPY.cancel}</button>
        </div>
        {line && <p className="acc-msg" role="alert">{line}</p>}
      </form>
    )
  }
  return (
    <div className="acc-note">
      {row.note && <p className="acc-note-text"><span className="acc-note-label">{COPY.yourNote}</span>{row.note}</p>}
      <button type="button" className="acc-link" onClick={start} {...readOnlyControl(viewingAs)}>
        {row.note ? COPY.changeNote : COPY.addNote}
      </button>
      {line && <p className="acc-msg" role="alert">{line}</p>}
    </div>
  )
}
